import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icons } from "../components/ui/icons";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import SelectInput from "../components/select-input";
import SearchInput from "../components/search-input";
import DateRangePicker from "../components/date-range-picker";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable, { TableRowData } from "../components/data-table";
import { activitiesTableColumns } from "../lib/table-columns";
import ActivityDetailsDrawer from "../components/activity-details-drawer";
import DeliveryNoteDrawer from "../components/delivery-note-drawer";
import ReturningNoteDrawer from "../components/returning-note-drawer";
import SellNoteDrawer from "../components/sell-note-drawer";
import TransferDetailsDrawer from "../components/transfer-details-drawer";
import WarehouseTransferDetailsDrawer from "../components/warehouse-transfer-details-drawer";
import DetailsOfReceivedDrawer from "../components/details-of-received-drawer";
import LostItemsDetailsDrawer from "../components/lost-items-details-drawer";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import {
  getMovementActivities,
  getMovementActivityStatistics,
  getWarehouses,
  MovementActivityStatistics,
  MovementActivity,
  Warehouse,
} from "../lib/api";
import { toast } from "sonner";
import { useUser } from "../hooks/use-user";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { getBackendErrorMessage } from "../lib/utils";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";

/** Stacked segment colors (same palette order as Warehouses inventory graph). */
const ACTIVITY_SUBTYPE_STACK_COLORS = [
  "#bce3ff",
  "#e9d5ff",
  "#ffd6a8",
  "#a7f3d0",
  "#c4b5fd",
  "#fecdd3",
  "#fcd34d",
  "#93c5fd",
];

/** How activities + stats are scoped to the warehouse API (mirrors Inventory). */
type ActivitiesDataQuery =
  | { status: "pending" }
  | { status: "empty" }
  | { status: "scoped"; warehouseId: string }
  | { status: "all" };

function warehouseIdForActivitiesApi(q: ActivitiesDataQuery): string | undefined {
  if (q.status === "scoped") return q.warehouseId;
  if (q.status === "all") return undefined;
  return undefined;
}

const getSourceName = (a: MovementActivity) =>
  a.sourceSite
    ? [a.sourceSite.customer?.fullName, a.sourceSite.name]
      .filter(Boolean)
      .join(" - ")
    : a.sourceWarehouse?.name || a.sourceVendor?.fullName || "N/A";

const getDestinationName = (a: MovementActivity) =>
  a.destinationSite
    ? [a.destinationSite.customer?.fullName, a.destinationSite.name]
      .filter(Boolean)
      .join(" - ")
    : a.destinationWarehouse?.name || a.destinationCustomer?.fullName || "N/A";

type BlankNoteType =
  | "delivery"
  | "returning"
  | "sell"
  | "transfer"
  | "warehouse-transfer"
  | "details-of-received"
  | "lost-items";

const BLANK_NOTE_OPTIONS: Array<{ type: BlankNoteType; label: string }> = [
  { type: "delivery", label: "Blank Delivery Note" },
  { type: "returning", label: "Blank Returning Note" },
  { type: "sell", label: "Blank Sell Note" },
  { type: "transfer", label: "Blank Transfer Details" },
  { type: "warehouse-transfer", label: "Blank Warehouse Transfer Details" },
  { type: "details-of-received", label: "Blank Details of Received" },
  { type: "lost-items", label: "Blank Lost Items Note" },
];

const Activities: React.FC = () => {
  const location = useLocation();
  const state = location.state as
    | { newMovement?: TableRowData; id?: string }
    | null
    | undefined;
  const navigate = useNavigate();
  const { user } = useUser();
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedActivityDetails, setSelectedActivityDetails] = useState<TableRowData | null>(null);
  const [blankNoteType, setBlankNoteType] = useState<BlankNoteType | null>(null);

  const [status, setStatus] = useState("all");
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    typeof state?.id === "string" ? state.id : "",
  );
  /** When true, user explicitly chose "All warehouses" in the filter. */
  const [viewsAllWarehouses, setViewsAllWarehouses] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getWarehouses(1, 1000, ""),
  });

  const warehouses = warehousesQuery.data?.data ?? [];

  const visibleWarehouses = useMemo(() => {
    if (!user || user.role === "ADMIN") return warehouses;
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const uCountry = norm(user.country);
    const uRegion = norm(user.region);
    const uDistrict = norm(user.district);
    const uWard = norm(user.ward);
    const uStreet = norm(user.street);
    if (!uCountry && !uRegion && !uDistrict && !uWard && !uStreet) {
      return warehouses;
    }
    return warehouses.filter((w) => {
      if (uCountry && norm(w.country) !== uCountry) return false;
      if (uRegion && norm(w.region) !== uRegion) return false;
      if (uDistrict && norm(w.district) !== uDistrict) return false;
      if (uWard && norm(w.ward) !== uWard) return false;
      if (uStreet && norm(w.street) !== uStreet) return false;
      return true;
    });
  }, [warehouses, user]);

  const defaultWarehouseId = useMemo(() => {
    if (visibleWarehouses.length === 0) return "";
    let best = visibleWarehouses[0];
    let bestWeight = best.totalWeight ?? 0;
    for (const w of visibleWarehouses) {
      const tw = w.totalWeight ?? 0;
      if (tw > bestWeight) {
        bestWeight = tw;
        best = w;
      }
    }
    return best.id;
  }, [visibleWarehouses]);

  const warehouseFilterOptions = useMemo(() => {
    const opts = visibleWarehouses.map((w) => ({
      label: w.name,
      value: w.id,
    }));
    return [{ label: "All", value: "All" }, ...opts];
  }, [visibleWarehouses]);

  const activitiesDataQuery = useMemo((): ActivitiesDataQuery => {
    if (!warehousesQuery.isSuccess) return { status: "pending" };
    if (visibleWarehouses.length === 0) return { status: "empty" };
    if (selectedWarehouse) return { status: "scoped", warehouseId: selectedWarehouse };
    if (viewsAllWarehouses) return { status: "all" };
    return { status: "scoped", warehouseId: defaultWarehouseId };
  }, [
    warehousesQuery.isSuccess,
    visibleWarehouses.length,
    selectedWarehouse,
    viewsAllWarehouses,
    defaultWarehouseId,
  ]);

  const warehouseSelectDisplayValue =
    viewsAllWarehouses
      ? "All"
      : selectedWarehouse || defaultWarehouseId;

  const handleWarehouseFilterChange = (value: string) => {
    if (value === "All") {
      setSelectedWarehouse("");
      setViewsAllWarehouses(true);
    } else {
      setSelectedWarehouse(value);
      setViewsAllWarehouses(false);
    }
  };

  useEffect(() => {
    if (!warehousesQuery.error) return;
    toast.error(getBackendErrorMessage(warehousesQuery.error, "Failed to fetch warehouses"));
  }, [warehousesQuery.error]);

  useEffect(() => {
    if (visibleWarehouses.length === 0 || !defaultWarehouseId) return;

    const navId =
      typeof state?.id === "string" ? state.id : undefined;

    const inVisible = (id: string) => visibleWarehouses.some((w) => w.id === id);

    if (!viewsAllWarehouses && navId && inVisible(navId)) {
      if (selectedWarehouse !== navId) setSelectedWarehouse(navId);
      setViewsAllWarehouses(false);
      return;
    }

    if (selectedWarehouse && inVisible(selectedWarehouse)) {
      setViewsAllWarehouses(false);
      return;
    }

    if (!selectedWarehouse && viewsAllWarehouses) {
      return;
    }

    setSelectedWarehouse(defaultWarehouseId);
    setViewsAllWarehouses(false);
  }, [
    defaultWarehouseId,
    visibleWarehouses,
    selectedWarehouse,
    state?.id,
    viewsAllWarehouses,
  ]);

  const activitySubTypeParam = currentTab !== "All" ? currentTab : undefined;
  const statusParam = status !== "all" ? status : undefined;
  const warehouseIdParam = warehouseIdForActivitiesApi(activitiesDataQuery);
  const dateFromParam = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const dateToParam = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

  const activitiesEnabled =
    activitiesDataQuery.status !== "pending" && activitiesDataQuery.status !== "empty";

  const activitiesQuery = useQuery({
    queryKey: [
      "activities",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        activitySubType: activitySubTypeParam,
        status: statusParam,
        warehouseId: warehouseIdParam,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      },
    ],
    queryFn: async () =>
      getMovementActivities(
        currentPage,
        pageSize,
        searchTerm,
        activitySubTypeParam,
        warehouseIdParam,
        dateFromParam,
        dateToParam,
        statusParam,
      ),
    enabled: activitiesEnabled,
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<MovementActivityStatistics>({
    queryKey: [
      "activities",
      "statistics",
      { warehouseId: warehouseIdParam, dateFrom: dateFromParam, dateTo: dateToParam },
    ],
    queryFn: async () => getMovementActivityStatistics(warehouseIdParam, dateFromParam, dateToParam),
    enabled: activitiesEnabled,
  });

  useEffect(() => {
    if (!activitiesQuery.error) return;
    toast.error(getBackendErrorMessage(activitiesQuery.error, "Failed to fetch activities"));
  }, [activitiesQuery.error]);

  useEffect(() => {
    if (!statisticsQuery.error) return;
    toast.error(getBackendErrorMessage(statisticsQuery.error, "Failed to fetch statistics"));
  }, [statisticsQuery.error]);

  const movementActivities = useMemo(() => {
    if (!activitiesEnabled) return [];
    const data = activitiesQuery.data?.data ?? [];
    const mapped = data.map((a) => ({
      id: a.id,
      reference: a.reference,
      date: new Date(a.createdAt).toLocaleDateString(),
      initiator: a.initiator.fullName,
      source: getSourceName(a),
      destination: getDestinationName(a),
      activity_type: a.activityType,
      activity_sub_type: a.activitySubType,
      project_type: a.projectType ?? "—",
      status: a.status,
      raw_data: a,
    })) as TableRowData[];

    return mapped;
  }, [activitiesEnabled, activitiesQuery.data]);

  const statistics = statisticsQuery.data ?? null;
  const totalItems = activitiesQuery.data?.meta.total ?? 0;
  const totalPages = activitiesQuery.data?.meta.totalPages ?? 1;
  const initialLoading =
    warehousesQuery.isLoading ||
    (activitiesEnabled && activitiesQuery.isLoading);
  const tableLoading = activitiesQuery.isFetching && !activitiesQuery.isLoading;

  // Reset pagination on tab, status, warehouse or date range changes (search handled separately).
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab, status, activitiesDataQuery, dateRange]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), reset pagination.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedActivityDetails(row);
      },
    },
  ];

  const statusMetrics =
    (statistics?.byStatus.map((s) => ({
      label: s.status,
      count: s.count,
      percentage: Math.round((s.count / (statistics?.totalMovements || 1)) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  const pendingMovementsCount = useMemo(() => {
    if (!statistics?.byStatus?.length) return 0;
    const terminal = new Set(["CLOSED", "CANCELLED"]);
    return statistics.byStatus.reduce((acc, s) => {
      const u = (s.status || "").toUpperCase();
      return terminal.has(u) ? acc : acc + s.count;
    }, 0);
  }, [statistics?.byStatus]);

  const activityTypeData = useMemo(() => {
    const byCombo = statistics?.byTypeAndSubType;
    if (!byCombo?.length) {
      return (
        statistics?.byActivityType.map((t) => ({
          category: t.activityType,
          gap: 0.2,
          values: [
            {
              label: "Count",
              value: t.count,
              fillColor: "#bce3ff",
            },
          ],
        })) ?? []
      );
    }

    const byType = new Map<string, Map<string, number>>();
    for (const row of byCombo) {
      const t = (row.activityType || "—").trim() || "—";
      const st = (row.activitySubType || "—").trim() || "—";
      if (!byType.has(t)) byType.set(t, new Map());
      const m = byType.get(t)!;
      m.set(st, (m.get(st) ?? 0) + row.count);
    }

    const subtypeTotals = new Map<string, number>();
    for (const row of byCombo) {
      const st = (row.activitySubType || "—").trim() || "—";
      subtypeTotals.set(st, (subtypeTotals.get(st) ?? 0) + row.count);
    }
    const orderedSubTypes = Array.from(subtypeTotals.entries())
      .filter(([, c]) => c > 0)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([k]) => k);

    if (orderedSubTypes.length === 0) {
      return (
        statistics?.byActivityType.map((t) => ({
          category: t.activityType,
          gap: 0.2,
          values: [
            {
              label: "Count",
              value: t.count,
              fillColor: "#bce3ff",
            },
          ],
        })) ?? []
      );
    }

    const subtypeColor = new Map<string, string>();
    orderedSubTypes.forEach((st, i) => {
      subtypeColor.set(st, ACTIVITY_SUBTYPE_STACK_COLORS[i % ACTIVITY_SUBTYPE_STACK_COLORS.length]);
    });

    const typeTotals = Array.from(byType.entries())
      .map(([activityType, subs]) => ({
        activityType,
        total: Array.from(subs.values()).reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => b.total - a.total);

    return typeTotals.map(({ activityType }) => {
      const subs = byType.get(activityType)!;
      const values = orderedSubTypes.map((st) => ({
        label: st,
        value: subs.get(st) ?? 0,
        fillColor: subtypeColor.get(st) ?? "#bce3ff",
      }));
      return {
        category: activityType,
        gap: 0.2,
        values,
      };
    });
  }, [statistics]);

  const displayedActivities = useMemo(() => {
    const nm = state?.newMovement;
    if (!nm) return movementActivities;
    if (movementActivities.some((a) => a.id === nm.id)) return movementActivities;
    return [nm, ...movementActivities];
  }, [movementActivities, state?.newMovement]);

  useEffect(() => {
    if (!selectedActivityDetails) return;
    const next = movementActivities.find((a) => a.id === selectedActivityDetails.id);
    if (next) setSelectedActivityDetails(next);
  }, [movementActivities, selectedActivityDetails]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["activities"] }),
      queryClient.invalidateQueries({ queryKey: ["warehouses"] }),
    ]);

  const openNewMovement = () => {
    navigate("/add-activity");
  };

  const homeWarehouseId =
    typeof state?.id === "string" && visibleWarehouses.some((w) => w.id === state.id)
      ? state.id
      : defaultWarehouseId;

  const activeFilterCount =
    (viewsAllWarehouses || warehouseSelectDisplayValue !== homeWarehouseId ? 1 : 0) +
    (status !== "all" ? 1 : 0) +
    (dateRange?.from ? 1 : 0);

  const resetFilters = () => {
    setSelectedWarehouse(homeWarehouseId);
    setViewsAllWarehouses(false);
    setStatus("all");
    setDateRange(undefined);
  };

  const activeWarehouseName = viewsAllWarehouses
    ? "All warehouses"
    : visibleWarehouses.find((w) => w.id === warehouseSelectDisplayValue)?.name;

  const warehouseSelect = (
    <SelectInput
      value={warehouseSelectDisplayValue}
      options={warehouseFilterOptions}
      placeholder="Warehouse"
      onChange={handleWarehouseFilterChange}
    />
  );

  const statusSelect = (
    <SelectInput
      value={status}
      options={[
        { label: "All", value: "all" },
        { label: "Draft", value: "DRAFT" },
        { label: "Initiated", value: "INITIATED" },
        { label: "Approved", value: "APPROVED" },
        { label: "Closed", value: "CLOSED" },
        { label: "Cancelled", value: "CANCELLED" },
      ]}
      placeholder={"Status"}
      onChange={(value) => {
        setStatus(value);
      }}
      className="ml-auto"
    />
  );

  return (
    <>
      <ActivityDetailsDrawer
        selectedActivityDetails={selectedActivityDetails}
        onClose={() => {
          setSelectedActivityDetails(null);
        }}
        onRefresh={async () => {
          await queryClient.invalidateQueries({ queryKey: ["activities"] });
        }}
      />

      <DeliveryNoteDrawer
        movementData={undefined}
        deliveryNoteDetails={blankNoteType === "delivery" ? { blank: true } : null}
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <ReturningNoteDrawer
        movementData={undefined}
        returningNoteDetails={blankNoteType === "returning" ? { blank: true } : null}
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <SellNoteDrawer
        movementData={undefined}
        sellNoteDetails={blankNoteType === "sell" ? { blank: true } : null}
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <TransferDetailsDrawer
        movementData={undefined}
        transferDetails={blankNoteType === "transfer" ? { blank: true } : null}
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <WarehouseTransferDetailsDrawer
        movementData={undefined}
        warehouseTransferDetails={
          blankNoteType === "warehouse-transfer" ? { blank: true } : null
        }
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <DetailsOfReceivedDrawer
        movementData={undefined}
        detailsOfReceived={
          blankNoteType === "details-of-received" ? { blank: true } : null
        }
        blank
        onClose={() => setBlankNoteType(null)}
      />
      <LostItemsDetailsDrawer
        movementData={undefined}
        lostItemsDetails={blankNoteType === "lost-items" ? { blank: true } : null}
        blank
        onClose={() => setBlankNoteType(null)}
      />

      <PageShell
        title="Movement Activities"
        description="Track and manage inventory activities across all sites."
        loading={initialLoading}
        toolbar={
          <>
            {warehouseSelect}
            <DateRangePicker
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
              }}
              className="shrink-0"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-9">
                  <Icons.Download className="h-4 w-4 mr-2" />
                  Blank Notes
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {BLANK_NOTE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.type}
                    onClick={() => setBlankNoteType(option.type)}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <RefreshToolbarButton
              isRefreshing={
                activitiesQuery.isFetching ||
                statisticsQuery.isFetching ||
                warehousesQuery.isFetching
              }
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewMovement}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Movement
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Movements",
            value: statistics?.totalMovements.toLocaleString() || "0",
            icon: <Icons.InStock className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Closed Movements",
            value: statistics?.closedMovements.toLocaleString() || "0",
            icon: <Icons.Close className="w-5 h-5 text-green-600" />,
          },
          {
            label: "Cancelled Movements",
            value: (statistics?.cancelledMovements ?? 0).toLocaleString(),
            icon: <Icons.Close className="w-5 h-5 text-red-600" />,
          },
          {
            label: "Pending Movements",
            value: pendingMovementsCount.toLocaleString(),
            icon: <Icons.Clock className="w-5 h-5 text-orange-600" />,
          },
        ]}
        statsColumns={4}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4 mt-6">
            <HorizontalBarGraph
              graphTitle="Activities by movement type"
              data={activityTypeData.slice(0, 3)}
            />
            <BarGraph
              graphTitle="Activities by Status"
              metrics={statusMetrics.slice(0, 3)}
            />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Movement", icon: Icons.Plus, onClick: openNewMovement },
          actions: BLANK_NOTE_OPTIONS.map((option) => ({
            label: option.label,
            icon: Icons.Download,
            onClick: () => setBlankNoteType(option.type),
          })),
          filters: (
            <>
              <FilterField label="Warehouse">{warehouseSelect}</FilterField>
              <FilterField label="Status">{statusSelect}</FilterField>
              <FilterField label="Date range">
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  className="h-11 w-full justify-start"
                />
              </FilterField>
            </>
          ),
          activeFilterCount,
          onResetFilters: resetFilters,
          headerContent: activeWarehouseName && (
            <span className="inline-flex max-w-full items-center truncate rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {activeWarehouseName}
            </span>
          ),
        }}
      >
        <TabBar>
          {[
            { label: "All", count: statistics?.totalMovements || 0 },
            ...statistics?.byActivitySubType.map(s => ({ label: s.activitySubType, count: s.count })) || [],
          ].map((tab) => (
            <TabButton
              key={tab.label}
              label={tab.label}
              count={tab.count}
              active={currentTab === tab.label}
              onClick={() => {
                setCurrentTab(tab.label);
              }}
            />
          ))}
        </TabBar>
        <div className="flex flex-wrap items-center gap-2 w-full">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={(value) => setSearchInput(value)}
            placeholder="Search movements..."
          />
          <DesktopOnly>{statusSelect}</DesktopOnly>
        </div>
        <DataTable
          columns={activitiesTableColumns}
          data={displayedActivities}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Activities;
